/**
 * Semis, recherche et recolte des noeuds de ressource.
 *
 * Le terrain est recu comme ITerrain (l'interface du projet) et pas comme la
 * classe Terrain: ca garde ce module independant du generateur de terrain, donc
 * testable seul, et un Terrain reste evidemment acceptable en argument.
 *
 * Les `id` sont l'index d'insertion: a graine egale, le meme noeud porte le meme
 * id sur le client et sur le serveur. Le serveur ne persiste donc que la liste
 * des noeuds epuises (id + repousseA), jamais la geometrie.
 */
import { DAY_LENGTH_MS, ILE2_COTE, ILE2_X0, ILE2_Z0, REPOUSSE_JOURS, WORLD_SIZE } from '../constants.ts'
import type { Cout, ItemId } from '../types/items.ts'
import type { Batiment, Ressource, RessourceKind } from '../types/monde.ts'
import type { BiomeId, ITerrain } from '../types/world.ts'
import type { RegionDef } from '../types/ile2.ts'
import { RAYON_AERODROME, SITE_AERODROME } from '../data/ile2-regions.ts'
import { RESSOURCES } from '../data/ressources.ts'
import { couvertParBatiment } from './batiments.ts'
import { dansLeLitDeLaChute } from './terrain.ts'
import { Alea, dist2, melangerCouleur } from './alea.ts'

// ------------------------------------------------------------------- semis

/** Pas de la grille de candidats, en metres. */
const PAS_SEMIS = 4

/** Distance minimale entre deux noeuds, en metres. */
const ESPACEMENT_MIN = 2.4

/** Marge sans noeud au bord du monde. */
const MARGE_BORD = 6

/** Pente maximale acceptee pour un noeud. */
const PENTE_MAX = 0.75

/** Rayon sans noeud autour du point d'apparition du joueur (250, 250). */
const RAYON_CLAIRIERE = 7

/**
 * L'ile 2: des candidats tires au hasard plutot qu'une grille balayee.
 *
 * Une quinzaine d'arbres sur 200 m de cote, c'est « quelques arbres » et non
 * une foret: la grille jitteree du monde 1 serait un outil demesure pour ca, et
 * surtout elle lierait le semis a un pas qu'il faudra revoir quand l'ile
 * grandira. Une marge tient les palmiers a l'ecart de la plage, ou l'on entre
 * dans l'eau, et de l'aeroport, qui a besoin de sa piste degagee.
 */
const MARGE_ILE2 = 12

/** Probabilite d'accepter un candidat, par biome. */
const DENSITE: Record<BiomeId, number> = {
  // La foret tropicale doit etre dense: c'est le coeur du monde et on doit s'y
  // sentir sous les arbres, pas dans un verger.
  foret: 0.62,
  plage: 0.1,
  montagne: 0.24,
  glacier: 0.17,
  desert: 0.13,
  // Les trois biomes de l'ile 2. Ils ne servent que la-bas, ou c'est la REGION
  // qui decide vraiment (`RegionDef.densiteNoeuds`); ces valeurs sont le repli
  // pour qui interrogerait la table par le biome.
  cristal: 0.34,
  jungle: 0.66,
  ferme: 0.12,
}

/** Poids relatifs des especes de noeud, par biome. */
const MELANGE: Record<BiomeId, readonly { kind: RessourceKind; poids: number }[]> = {
  foret: [
    { kind: 'arbre', poids: 0.72 },
    { kind: 'buisson_baies', poids: 0.28 },
  ],
  plage: [{ kind: 'palmier', poids: 1 }],
  montagne: [
    { kind: 'rocher', poids: 0.62 },
    { kind: 'arbre', poids: 0.38 },
  ],
  glacier: [
    { kind: 'bloc_glace', poids: 0.62 },
    { kind: 'rocher', poids: 0.38 },
  ],
  desert: [{ kind: 'cactus', poids: 1 }],
  cristal: [
    { kind: 'cristal', poids: 0.6 },
    { kind: 'rocher', poids: 0.4 },
  ],
  jungle: [
    { kind: 'arbre', poids: 0.5 },
    { kind: 'palmier', poids: 0.28 },
    { kind: 'buisson_baies', poids: 0.22 },
  ],
  ferme: [
    { kind: 'arbre', poids: 0.6 },
    { kind: 'buisson_baies', poids: 0.4 },
  ],
}

/** Teinte de base par espece, nuancee ensuite par individu. */
const TEINTE: Record<RessourceKind, { a: number; b: number }> = {
  arbre: { a: 0x2f7d32, b: 0x5eb04a },
  palmier: { a: 0x3f8f4a, b: 0x76bd5c },
  rocher: { a: 0x7d7d86, b: 0xa6a6b0 },
  buisson_baies: { a: 0x3d7a34, b: 0x6aa84f },
  cactus: { a: 0x4a7a3c, b: 0x7fa85a },
  bloc_glace: { a: 0x9fdcf0, b: 0xd8f4ff },
  cristal: { a: 0x9a86d8, b: 0xd9c9f4 },
  arbre_lilas: { a: 0x8f5fb5, b: 0xd9a8e8 },
}

function choisirKind(biome: BiomeId, r: number): RessourceKind | null {
  return choisirParmi(MELANGE[biome], r)
}

/** Tire une espece dans un melange pondere. Les poids somment a 1. */
function choisirParmi(melange: readonly { kind: RessourceKind; poids: number }[], r: number): RessourceKind | null {
  let cumul = 0
  for (const entree of melange) {
    cumul += entree.poids
    if (r < cumul) return entree.kind
  }
  const dernier = melange[melange.length - 1]
  return dernier ? dernier.kind : null
}

/** Grille d'occupation pour faire respecter l'espacement minimal. */
class GrilleEspacement {
  private cases = new Map<number, { x: number; z: number }[]>()
  private readonly pas = ESPACEMENT_MIN

  private cle(cx: number, cz: number): number {
    return cx * 4096 + cz
  }

  libre(x: number, z: number): boolean {
    const cx = Math.floor(x / this.pas)
    const cz = Math.floor(z / this.pas)
    const min2 = ESPACEMENT_MIN * ESPACEMENT_MIN
    for (let dx = -1; dx <= 1; dx++) {
      for (let dz = -1; dz <= 1; dz++) {
        const liste = this.cases.get(this.cle(cx + dx, cz + dz))
        if (!liste) continue
        for (const p of liste) {
          if (dist2(x, z, p.x, p.z) < min2) return false
        }
      }
    }
    return true
  }

  poser(x: number, z: number): void {
    const k = this.cle(Math.floor(x / this.pas), Math.floor(z / this.pas))
    const liste = this.cases.get(k)
    if (liste) liste.push({ x, z })
    else this.cases.set(k, [{ x, z }])
  }
}

/**
 * Seme les noeuds recoltables. Un seul balayage d'une grille jitteree: l'ordre
 * de parcours est fige (x croissant, puis z croissant), donc les id sont stables
 * pour une graine donnee.
 */
export function genererRessources(terrain: ITerrain, seed: number): Ressource[] {
  const alea = new Alea((seed ^ 0x5ca1ab1e) >>> 0)
  const espacement = new GrilleEspacement()
  const sortie: Ressource[] = []
  const cases = Math.floor(WORLD_SIZE / PAS_SEMIS)

  for (let ix = 0; ix < cases; ix++) {
    for (let iz = 0; iz < cases; iz++) {
      // On tire toujours le meme nombre de valeurs par case: le flux d'aleatoire
      // ne depend pas des rejets, donc la generation reste stable.
      const jx = alea.entre(-1.6, 1.6)
      const jz = alea.entre(-1.6, 1.6)
      const dDensite = alea.reel()
      const dKind = alea.reel()
      const dScale = alea.entre(0.82, 1.24)
      const dRot = alea.entre(0, Math.PI * 2)
      const dTeinte = alea.reel()

      const x = ix * PAS_SEMIS + PAS_SEMIS / 2 + jx
      const z = iz * PAS_SEMIS + PAS_SEMIS / 2 + jz
      if (x < MARGE_BORD || x > WORLD_SIZE - MARGE_BORD) continue
      if (z < MARGE_BORD || z > WORLD_SIZE - MARGE_BORD) continue
      if (dist2(x, z, 250, 250) < RAYON_CLAIRIERE * RAYON_CLAIRIERE) continue

      const ech = terrain.echantillon(x, z)
      if (ech.sol === 'eau' || terrain.estEau(x, z)) continue
      if (ech.pente > PENTE_MAX) continue
      // Le lit de la chute est plat et marchable: sans cette exception, un
      // arbre y pousse au milieu du courant.
      if (dansLeLitDeLaChute(terrain, x, z)) continue
      if (dDensite > DENSITE[ech.biome]) continue

      const kind = choisirKind(ech.biome, dKind)
      if (kind === null) continue
      if (!RESSOURCES[kind].biomes.includes(ech.biome)) continue
      if (!espacement.libre(x, z)) continue

      espacement.poser(x, z)
      const teinte = TEINTE[kind]
      sortie.push({
        id: sortie.length,
        kind,
        pos: { x, z },
        y: terrain.hauteur(x, z),
        rot: dRot,
        scale: dScale,
        teinte: melangerCouleur(teinte.a, teinte.b, dTeinte),
        pv: RESSOURCES[kind].coups,
        repousseA: 0,
      })
    }
  }

  semerIle2(terrain, alea, espacement, sortie)
  return sortie
}

/**
 * LE SEMIS DE L'ILE 2 — onze regions, et une seule regle.
 *
 * Il est appele APRES la boucle du monde 1, et c'est la chose la plus
 * importante de ce fichier: les `id` sont des index d'insertion, donc tout
 * noeud du monde 1 garde exactement le sien. Une sauvegarde qui dit « le noeud
 * 3172 est epuise jusqu'au jour 41 » continue de parler du meme arbre. Semer
 * l'ile 2 en premier, ou intercaler, deplacerait chaque souche du monde.
 *
 * L'ile portait vingt-deux palmiers tires au hasard — un nombre ABSOLU, qui
 * aurait donne vingt-deux arbres sur quarante-neuf hectares. C'est desormais le
 * meme balayage regulier que le monde 1, au meme pas, donc la densite suit la
 * SURFACE toute seule: agrandir l'ile ne demande plus de retoucher un compteur.
 *
 * Et c'est la REGION qui decide de ce qui pousse, jamais un nom de modele ecrit
 * en dur. Le garde-fou `RESSOURCES[kind].biomes` reste: la region dit ce
 * qu'elle VEUT, la table des especes dit ou l'espece a le droit d'etre.
 */
function semerIle2(terrain: ITerrain, alea: Alea, espacement: GrilleEspacement, sortie: Ressource[]): void {
  const cases = Math.floor(ILE2_COTE / PAS_SEMIS)
  const rayonAero2 = RAYON_AERODROME * RAYON_AERODROME

  for (let ix = 0; ix < cases; ix++) {
    for (let iz = 0; iz < cases; iz++) {
      // Tous les tirages AVANT tout rejet: le flux d'aleatoire ne depend que de
      // la graine, jamais du terrain rencontre.
      const jx = alea.entre(-1.6, 1.6)
      const jz = alea.entre(-1.6, 1.6)
      const dDensite = alea.reel()
      const dKind = alea.reel()
      const dScale = alea.entre(0.82, 1.24)
      const dRot = alea.entre(0, Math.PI * 2)
      const dTeinte = alea.reel()

      const x = ILE2_X0 + ix * PAS_SEMIS + PAS_SEMIS / 2 + jx
      const z = ILE2_Z0 + iz * PAS_SEMIS + PAS_SEMIS / 2 + jz
      if (x < ILE2_X0 + MARGE_ILE2 || x > ILE2_X0 + ILE2_COTE - MARGE_ILE2) continue
      if (z < ILE2_Z0 + MARGE_ILE2 || z > ILE2_Z0 + ILE2_COTE - MARGE_ILE2) continue
      // Le couloir de la piste reste degage: `validerPlacement` refuse de batir
      // sur un noeud vivant, et un seul arbre en travers suffirait a ce que le
      // seul aeroport du monde 2 ne se pose jamais.
      if (dist2(x, z, SITE_AERODROME.x, SITE_AERODROME.z) < rayonAero2) continue

      const ech = terrain.echantillon(x, z)
      if (ech.sol === 'eau' || terrain.estEau(x, z)) continue
      if (ech.pente > PENTE_MAX) continue
      if (dansLeLitDeLaChute(terrain, x, z)) continue

      const region = regionAuPoint(terrain, x, z)
      if (!region) continue
      if (dDensite > region.densiteNoeuds) continue

      const kind = choisirParmi(region.melange, dKind)
      if (kind === null) continue
      if (!RESSOURCES[kind].biomes.includes(ech.biome)) continue
      if (!espacement.libre(x, z)) continue

      espacement.poser(x, z)
      const teinte = TEINTE[kind]
      sortie.push({
        id: sortie.length,
        kind,
        pos: { x, z },
        y: terrain.hauteur(x, z),
        rot: dRot,
        scale: dScale,
        teinte: melangerCouleur(teinte.a, teinte.b, dTeinte),
        pv: RESSOURCES[kind].coups,
        repousseA: 0,
      })
    }
  }
}

/**
 * La region de l'ile 2 sous ce point, ou null si le terrain ne sait pas
 * repondre (un faux terrain de test, par exemple): on ne seme alors rien plutot
 * que d'inventer une region.
 */
function regionAuPoint(terrain: ITerrain, x: number, z: number): RegionDef | null {
  const lecteur = terrain as { regionAuPoint?: (x: number, z: number) => RegionDef | null }
  if (typeof lecteur.regionAuPoint !== 'function') return null
  return lecteur.regionAuPoint(x, z)
}

// --------------------------------------------------------------- disponibilite

/**
 * Vrai si le noeud est la, visible et recoltable, maintenant.
 *
 * `batiments` est FACULTATIF, et l'omettre veut dire « je ne sais rien des
 * constructions »: les tests purs et les appels isoles s'en passent. Quand la
 * liste est fournie, un noeud recouvert par une construction n'existe plus pour
 * personne — ni pour le rendu, ni pour la recolte.
 *
 * POURQUOI ICI. `validerPlacement` refuse de batir sur un noeud VIVANT: le
 * joueur abat donc l'arbre, batit, et trois jours plus tard l'arbre repoussait
 * au milieu de sa cuisine. On ne peut pas le regler en effacant le noeud — la
 * generation vient de la graine, et demolir doit rendre l'arbre. On l'exprime
 * donc a la question, pas dans la donnee: tant que la maison est debout, la
 * repousse attend; le jour ou elle tombe, l'arbre est deja la.
 *
 * Le test s'applique AUSSI a un noeud vivant, alors que `validerPlacement`
 * l'interdit deja: c'est le meme filet que partout ailleurs dans ce projet —
 * une seule regle, appliquee sans exception, plutot que deux qui finiront par
 * diverger. En pratique il ne se declenche que sur une repousse.
 */
export function estDisponible(r: Ressource, tempsJeu: number, batiments?: readonly Batiment[]): boolean {
  if (batiments !== undefined && couvertParBatiment(batiments, r.pos.x, r.pos.z)) return false
  if (r.pv > 0) return true
  return r.repousseA > 0 && tempsJeu >= r.repousseA
}

/**
 * Remet a neuf un noeud dont la repousse est echue. Sert a ne pas dependre de
 * l'appel de `repousser`: un noeud du echu se comporte comme un noeud intact.
 */
function reveiller(r: Ressource, tempsJeu: number): void {
  if (r.pv <= 0 && r.repousseA > 0 && tempsJeu >= r.repousseA) {
    r.pv = RESSOURCES[r.kind].coups
    r.repousseA = 0
  }
}

/**
 * Remet a neuf les noeuds echus et renvoie CEUX qui ont repousse.
 *
 * Le rendu s'en sert pour ne rafraichir que les instances concernees: tout
 * reecrire reteleversait les 2 x 5229 matrices en une seule image.
 */
export function repousserListe(
  ressources: Ressource[],
  tempsJeu: number,
  batiments?: readonly Batiment[],
): Ressource[] {
  const reveilles: Ressource[] = []
  for (const r of ressources) {
    if (r.pv <= 0 && r.repousseA > 0 && tempsJeu >= r.repousseA) {
      // Un noeud sous une construction ne se reveille pas: son echeance reste
      // en attente, et le balayage suivant le prendra le jour ou la maison
      // tombera. Rien n'est perdu, rien n'est ecrit.
      if (batiments !== undefined && couvertParBatiment(batiments, r.pos.x, r.pos.z)) continue
      reveiller(r, tempsJeu)
      reveilles.push(r)
    }
  }
  return reveilles
}

/** Remet a neuf tous les noeuds echus. Renvoie le nombre de repousses. */
export function repousser(ressources: Ressource[], tempsJeu: number, batiments?: readonly Batiment[]): number {
  return repousserListe(ressources, tempsJeu, batiments).length
}

// ------------------------------------------------------------------- recolte

export interface ResultatRecolte {
  ressource: Ressource
  gains: Cout
  epuise: boolean
}

/** Chance qu'un rocher rende un minerai en plus du dernier coup. */
export const CHANCE_MINERAI = 1 / 3

/**
 * Chance qu'un arbre abattu decouvre des champignons a son pied. C'est la seule
 * source de `champignon`: sans elle, l'objet existerait sans etre trouvable.
 */
export const CHANCE_CHAMPIGNON = 1 / 3

/**
 * En combien de journees de jeu CE noeud-la repousse.
 *
 * La reponse est dans la DONNEE (`RessourceDef.repousseJours`), avec
 * `REPOUSSE_JOURS` pour tous ceux qui ne disent rien — la meme regle que la
 * table `recolte` d'un cultivable. Une seule fonction pour la question, parce
 * que le rendu, la simulation et les tests doivent tomber sur le meme nombre.
 */
export function repousseJoursDe(kind: RessourceKind): number {
  return RESSOURCES[kind].repousseJours ?? REPOUSSE_JOURS
}

/**
 * Porte un coup. Mute le noeud (il appartient au monde) et rend ce qui tombe.
 * Rien ne tombe avant le coup final; les noeuds a un seul coup (cueillette)
 * rendent donc tout d'un coup.
 */
export function frapper(r: Ressource, tempsJeu: number, alea: Alea): ResultatRecolte {
  reveiller(r, tempsJeu)
  if (r.pv <= 0) return { ressource: r, gains: {}, epuise: true }

  r.pv -= 1
  if (r.pv > 0) return { ressource: r, gains: {}, epuise: false }

  r.repousseA = tempsJeu + repousseJoursDe(r.kind) * DAY_LENGTH_MS
  return { ressource: r, gains: recolteFinale(r.kind, alea), epuise: true }
}

/**
 * Ce que rend le coup FINAL sur un noeud: le lot fixe de la table, plus les
 * des (un minerai sous un rocher, des champignons au pied d'un arbre).
 *
 * UNE SEULE ECRITURE, lue par le navigateur (`frapper`) et par le serveur
 * (`gainRessource`). Quand le sac est passe au serveur, celui-ci n'a recopie
 * que la table: les des sont restes dans `frapper`, dont le client jette le
 * resultat — un rocher n'a plus jamais rendu de minerai, ni un arbre de
 * champignon, sans que rien ne le signale.
 */
export function recolteFinale(kind: RessourceKind, alea: Alea): Cout {
  const def = RESSOURCES[kind]
  const gains: Cout = {}
  for (const cle of Object.keys(def.recolte) as ItemId[]) {
    const n = def.recolte[cle] ?? 0
    if (n > 0) gains[cle] = n
  }
  if (kind === 'rocher' && alea.chance(CHANCE_MINERAI)) {
    gains.minerai = (gains.minerai ?? 0) + 1
  }
  if (kind === 'arbre' && alea.chance(CHANCE_CHAMPIGNON)) {
    gains.champignon = (gains.champignon ?? 0) + 1
  }
  return gains
}

// -------------------------------------------------------------------- index

/** Cote d'une case de l'index spatial, en metres. */
const CASE_INDEX = 8

/**
 * Index spatial des noeuds. `plusProche` est appele a chaque image pour la
 * surbrillance, donc on ne balaie que les cases utiles au lieu du tableau.
 */
export class IndexRessources {
  private cases = new Map<number, Ressource[]>()
  private parId = new Map<number, Ressource>()

  constructor(ressources: Ressource[]) {
    for (const r of ressources) this.inserer(r)
  }

  private cle(cx: number, cz: number): number {
    return cx * 4096 + cz
  }

  private inserer(r: Ressource): void {
    this.parId.set(r.id, r)
    const k = this.cle(Math.floor(r.pos.x / CASE_INDEX), Math.floor(r.pos.z / CASE_INDEX))
    const liste = this.cases.get(k)
    if (liste) liste.push(r)
    else this.cases.set(k, [r])
  }

  /** Nombre de noeuds indexes. */
  get taille(): number {
    return this.parId.size
  }

  /**
   * Signale qu'un noeud a change (recolte, repousse). Les noeuds ne bougent
   * jamais, donc il n'y a rien a reindexer; on l'ajoute s'il etait inconnu.
   */
  majNoeud(r: Ressource): void {
    const connu = this.parId.get(r.id)
    if (connu === undefined) this.inserer(r)
    else if (connu !== r) {
      // Un noeud restaure depuis la sauvegarde: on remplace la reference.
      const k = this.cle(Math.floor(connu.pos.x / CASE_INDEX), Math.floor(connu.pos.z / CASE_INDEX))
      const liste = this.cases.get(k)
      if (liste) {
        const i = liste.indexOf(connu)
        if (i >= 0) liste.splice(i, 1)
      }
      this.inserer(r)
    }
  }

  plusProche(
    x: number,
    z: number,
    portee: number,
    tempsJeu: number,
    batiments?: readonly Batiment[],
  ): Ressource | null {
    const rayon = Math.max(1, Math.ceil(portee / CASE_INDEX))
    const cx = Math.floor(x / CASE_INDEX)
    const cz = Math.floor(z / CASE_INDEX)
    let meilleur: Ressource | null = null
    let meilleurD2 = portee * portee
    for (let dx = -rayon; dx <= rayon; dx++) {
      for (let dz = -rayon; dz <= rayon; dz++) {
        const liste = this.cases.get(this.cle(cx + dx, cz + dz))
        if (!liste) continue
        for (const r of liste) {
          if (!estDisponible(r, tempsJeu, batiments)) continue
          const d2 = dist2(x, z, r.pos.x, r.pos.z)
          if (d2 < meilleurD2) {
            meilleurD2 = d2
            meilleur = r
          }
        }
      }
    }
    return meilleur
  }

  dansRayon(x: number, z: number, r: number, tempsJeu: number, batiments?: readonly Batiment[]): Ressource[] {
    const rayon = Math.max(1, Math.ceil(r / CASE_INDEX))
    const cx = Math.floor(x / CASE_INDEX)
    const cz = Math.floor(z / CASE_INDEX)
    const r2 = r * r
    const sortie: Ressource[] = []
    for (let dx = -rayon; dx <= rayon; dx++) {
      for (let dz = -rayon; dz <= rayon; dz++) {
        const liste = this.cases.get(this.cle(cx + dx, cz + dz))
        if (!liste) continue
        for (const n of liste) {
          if (!estDisponible(n, tempsJeu, batiments)) continue
          if (dist2(x, z, n.pos.x, n.pos.z) <= r2) sortie.push(n)
        }
      }
    }
    return sortie
  }
}

/**
 * Version sans index, pratique pour un appel isole ou un test. Pour la boucle
 * de rendu, passer par IndexRessources.
 */
export function ressourceLaPlusProche(
  ressources: Ressource[],
  x: number,
  z: number,
  portee: number,
  tempsJeu: number,
  batiments?: readonly Batiment[],
): Ressource | null {
  let meilleur: Ressource | null = null
  let meilleurD2 = portee * portee
  for (const r of ressources) {
    if (!estDisponible(r, tempsJeu, batiments)) continue
    const d2 = dist2(x, z, r.pos.x, r.pos.z)
    if (d2 < meilleurD2) {
      meilleurD2 = d2
      meilleur = r
    }
  }
  return meilleur
}

/**
 * CE QUI ARRETE LE PIETON: le tronc d'un arbre, un rocher, un bloc de glace.
 *
 * Le bonhomme traversait les rochers et les troncs (vu au telephone le 13
 * octobre; JC a tranche: « les rochers et les troncs bloquent »). Les
 * buissons, l'herbe et les fleurs restent franchissables: on les traverse
 * dans la vraie vie aussi. Le rayon est celui de la MASSE AU SOL, multiplie
 * par l'echelle du noeud — le tronc, pas la cime (voir `SILHOUETTE`).
 */
export const RAYON_SOLIDE: Partial<Record<RessourceKind, number>> = {
  arbre: 0.35,
  palmier: 0.3,
  arbre_lilas: 0.35,
  rocher: 0.8,
  cactus: 0.4,
  bloc_glace: 0.7,
  cristal: 0.5,
}

/** Ce qu'il faut au pas partage pour savoir ou sont les noeuds solides. */
export interface NoeudsSolides {
  index: IndexRessources
  tempsJeu: number
  batiments?: readonly Batiment[]
}

/** Au-dela, aucun noeud solide ne peut toucher le pieton (rocher a grande echelle compris). */
const PORTEE_SOLIDE = 3

/**
 * Repousse un point hors des noeuds SOLIDES vivants, radialement: on glisse
 * le long d'un tronc au lieu d'y rester colle. Rend `null` quand rien n'a
 * bouge — le retour d'une position recopiee n'est pas neutre pour qui la
 * compare au bit pres.
 *
 * Les noeuds sont traites PAR IDENTIFIANT croissant, pas dans l'ordre de
 * l'index: le navigateur et le serveur n'ont pas forcement insere leurs noeuds
 * dans le meme ordre, et deux poussees successives ne commutent pas.
 */
export function resoudreNoeuds(n: NoeudsSolides, pos: { x: number; z: number }, rayon: number): { x: number; z: number } | null {
  const proches = n.index
    .dansRayon(pos.x, pos.z, PORTEE_SOLIDE, n.tempsJeu, n.batiments)
    .filter((r) => RAYON_SOLIDE[r.kind] !== undefined)
  if (proches.length === 0) return null
  proches.sort((a, b) => a.id - b.id)
  let x = pos.x
  let z = pos.z
  let touche = false
  for (const r of proches) {
    const mini = (RAYON_SOLIDE[r.kind] ?? 0) * r.scale + rayon
    const dx = x - r.pos.x
    const dz = z - r.pos.z
    const d2 = dx * dx + dz * dz
    if (d2 >= mini * mini) continue
    const d = Math.sqrt(d2)
    // Pile au centre: une direction fixe, la meme des deux cotes du fil.
    if (d < 1e-6) x = r.pos.x + mini
    else {
      x = r.pos.x + (dx / d) * mini
      z = r.pos.z + (dz / d) * mini
    }
    touche = true
  }
  return touche ? { x, z } : null
}

/**
 * LE VOLUME QU'ON VOIT d'un noeud, pour le viser au doigt: un cylindre
 * vertical depuis son pied, `h` de haut et `r` de rayon, a l'echelle du noeud;
 * un arbre a sous sa cime un `tronc` mince, qui ne vole pas les taps vises
 * sur ce qui est derriere lui.
 * Les hauteurs sont celles du rendu (`HAUTEUR_MODELE`, client/render/
 * ressources.ts); les rayons, la demi-largeur de la masse la plus large.
 */
export const SILHOUETTE: Record<RessourceKind, { h: number; r: number; tronc?: { h: number; r: number } }> = {
  arbre: { h: 6.4, r: 1.5, tronc: { h: 2.2, r: 0.35 } },
  palmier: { h: 5.4, r: 1.3, tronc: { h: 3.6, r: 0.3 } },
  rocher: { h: 1.5, r: 1.0 },
  buisson_baies: { h: 1.3, r: 0.9 },
  cactus: { h: 2.8, r: 0.6 },
  bloc_glace: { h: 1.7, r: 0.9 },
  cristal: { h: 2.1, r: 0.7 },
  arbre_lilas: { h: 6.1, r: 1.5, tronc: { h: 2.1, r: 0.35 } },
}

/** Pas de la marche le long du rayon, en metres. */
const PAS_VISEE = 0.2

/**
 * LE NOEUD QUE LE DOIGT TOUCHE A L'ECRAN, s'il y en a un.
 *
 * Releve sur le telephone le 13 octobre: toucher le FEUILLAGE d'un arbre a
 * quatre metres du sol coupait le bloc de glace d'a cote. Le tap etait converti
 * en point du SOL, et le rayon qui traverse une cime ne touche le sol que
 * plusieurs metres DERRIERE le tronc — la ou se trouvait autre chose. Un arbre
 * se voit par sa cime; c'est elle qu'on touche.
 *
 * On marche le rayon depuis le haut des cimes jusqu'au sol (`tSol`) et l'on
 * rend le premier noeud dont la silhouette contient le point: le plus proche
 * de l'oeil, donc celui qui est DESSINE devant. `d` doit etre unitaire.
 */
export function noeudSousLeRayon(
  candidats: readonly Ressource[],
  o: { x: number; y: number; z: number },
  d: { x: number; y: number; z: number },
  tSol: number,
): Ressource | null {
  if (candidats.length === 0 || d.y >= 0) return null
  let haut = -Infinity
  for (const n of candidats) haut = Math.max(haut, n.y + SILHOUETTE[n.kind].h * n.scale)
  const t0 = Math.max(0, (haut - o.y) / d.y)
  for (let t = t0; t <= tSol; t += PAS_VISEE) {
    const x = o.x + d.x * t
    const y = o.y + d.y * t
    const z = o.z + d.z * t
    for (const n of candidats) {
      const s = SILHOUETTE[n.kind]
      if (y < n.y || y > n.y + s.h * n.scale) continue
      const r = (s.tronc && y < n.y + s.tronc.h * n.scale ? s.tronc.r : s.r) * n.scale
      if (dist2(x, z, n.pos.x, n.pos.z) <= r * r) return n
    }
  }
  return null
}
